import { createClient } from '@supabase/supabase-js';
import { frontendLogger } from '../services/frontendLogger';

/**
 * Production-ready Supabase configuration for frontend
 * Includes proper error handling, validation, and security measures
 */

// Validate required environment variables
const validateEnvironment = () => {
  const requiredVars = {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
    VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY
  };

  const missingVars = Object.entries(requiredVars)
    .filter(([, value]) => !value)
    .map(([key]) => key);

  if (missingVars.length > 0) {
    throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
  }

  return requiredVars;
};

// Get validated environment variables
const env = validateEnvironment();

// Supabase client configuration with security options
const supabaseConfig = {
  auth: {
    // Disable automatic refresh - we handle it manually
    autoRefreshToken: false,
    // Persist session in localStorage (secure for SPA)
    persistSession: true,
    // Detect session from URL hash
    detectSessionInUrl: true,
    // Storage key prefix for multi-tenancy support
    storageKey: 'sanctuary_auth',
    // Additional security options
    flowType: 'pkce', // Use PKCE flow for enhanced security
    retryDelay: 500 // Delay between retry attempts
  },
  global: {
    headers: {
      // Add security headers
      'X-Client-Info': 'sanctuary-frontend'
    }
  },
  // Database settings
  db: {
    // Schema for queries (if using different schema)
    schema: 'public'
  },
  // Real-time settings (disable if not needed for security)
  realtime: {
    params: {
      eventsPerSecond: 10 // Rate limit real-time events
    }
  }
};

// Create and export the Supabase client
export const supabase = createClient(
  env.VITE_SUPABASE_URL,
  env.VITE_SUPABASE_ANON_KEY,
  supabaseConfig
);

// Export configuration for potential use in other modules
export const config = {
  url: env.VITE_SUPABASE_URL,
  // Never expose the anon key directly - it's already in the client
  isProduction: import.meta.env.PROD,
  version: import.meta.env.VITE_APP_VERSION || '1.0.0'
};

// Connection health check (for development/monitoring)
export const checkConnection = async () => {
  try {
    const { error } = await supabase.from('profiles').select('count').limit(1);
    if (error) throw error;
    return { status: 'connected', timestamp: new Date().toISOString() };
  } catch (error) {
    return { status: 'disconnected', error: error.message, timestamp: new Date().toISOString() };
  }
};

// Auth helper functions for secure session management
export const authHelpers = {
  // Get current session with error handling
  getCurrentSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    } catch (error) {
      frontendLogger.error('Supabase failed to get current session', {
        component: 'SupabaseAuthHelpers',
        action: 'getCurrentSession',
        error: error.message,
        stack: error.stack
      });
      return null;
    }
  },

  // Get current user with error handling
  getCurrentUser: async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      return user;
    } catch {
      return null;
    }
  },

  // Sign out with cleanup
  signOut: async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      // Additional cleanup if needed
      localStorage.removeItem('sanctuary_token');
      localStorage.removeItem('authToken');
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};

// Session management for 24-hour timeout with activity tracking
export const sessionManager = {
  // Constants for session management
  SESSION_TIMEOUT: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
  ACTIVITY_CHECK_INTERVAL: 15 * 60 * 1000, // Check every 15 minutes - PERFORMANCE FIX
  TOKEN_REFRESH_INTERVAL: 50 * 60 * 1000, // Refresh token every 50 minutes (before 1-hour expiry)
  REFRESH_THRESHOLD: 10 * 60 * 1000, // Refresh if less than 10 minutes left (more aggressive)
  INACTIVITY_WARNING_TIME: 30 * 60 * 1000, // Show warning 30 minutes before 24-hour inactivity timeout

  // Track last activity
  lastActivity: Date.now(),
  activityTimer: null,
  sessionCheckTimer: null,
  tokenRefreshTimer: null,
  expiryCallbacks: new Set(), // Callbacks for session expiry events
  hasShownExpiryWarning: false,

  // Subscribe to session expiry events
  onSessionExpiry(callback) {
    this.expiryCallbacks.add(callback);
    return () => this.expiryCallbacks.delete(callback);
  },

  // Notify all listeners of session expiry
  notifySessionExpiry(type, data = {}) {
    this.expiryCallbacks.forEach(callback => {
      try {
        callback({ type, ...data });
      } catch (error) {
        frontendLogger.error('Supabase session expiry callback error', {
          component: 'SupabaseSessionManager',
          action: 'notifySessionExpiry',
          error: error.message,
          stack: error.stack
        });
      }
    });
  },

  // Update last activity timestamp
  updateActivity() {
    this.lastActivity = Date.now();
    localStorage.setItem('sanctuary_last_activity', this.lastActivity.toString());
  },

  // Check if custom JWT token should be refreshed based on activity
  async checkSessionValidity() {
    // Check for custom tokens (not Supabase sessions)
    const token = localStorage.getItem('sanctuary_token') || 
                 localStorage.getItem('authToken') || 
                 localStorage.getItem('token');
    
    if (!token) {
      return false;
    }

    try {
      // Decode JWT to get expiry (without verification - just for timing)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        return false;
      }
      
      const payload = JSON.parse(atob(tokenParts[1]));
      const now = Date.now();
      const timeSinceActivity = now - this.lastActivity;
      const expiresAt = payload.exp ? payload.exp * 1000 : 0;
      const timeUntilExpiry = expiresAt - now;

      // Check if we should show inactivity warning (based on actual inactivity, NOT token expiry)
      const timeUntilInactivityTimeout = this.SESSION_TIMEOUT - timeSinceActivity;
      
      if (timeSinceActivity > (this.SESSION_TIMEOUT - this.INACTIVITY_WARNING_TIME) && 
          timeSinceActivity < this.SESSION_TIMEOUT && 
          !this.hasShownExpiryWarning) {
        this.hasShownExpiryWarning = true;
        // Calculate when the 24-hour inactivity limit will be reached
        const inactivityTimeoutAt = this.lastActivity + this.SESSION_TIMEOUT;
        this.notifySessionExpiry('warning', {
          expiryTime: inactivityTimeoutAt,
          timeRemaining: timeUntilInactivityTimeout
        });
      }

      // If no activity for 24 hours, sign out
      if (timeSinceActivity > this.SESSION_TIMEOUT) {
        this.notifySessionExpiry('expired', { reason: 'inactivity' });
        await this.forceLogout();
        return false;
      }

      // If token expires soon and user is active, silently refresh it (NO user interruption)
      if (timeUntilExpiry < this.REFRESH_THRESHOLD && timeSinceActivity < this.SESSION_TIMEOUT) {
        try {
          // Import authService dynamically to refresh the custom token
          const { default: authService } = await import('../sanctuary/services/authService.js');
          await authService.refreshToken();
          this.updateActivity();
          this.hasShownExpiryWarning = false; // Reset warning flag on successful refresh
        } catch {
          // Only notify if token refresh fails AND user is still active (not due to inactivity)
          if (timeSinceActivity < this.SESSION_TIMEOUT) {
            this.notifySessionExpiry('expired', { reason: 'refresh_failed' });
            await this.forceLogout();
          }
          return false;
        }
      }

      // Predictive refresh - refresh when 75% of token lifetime has passed
      if (expiresAt > 0) {
        const tokenLifetime = expiresAt - (payload.iat ? payload.iat * 1000 : now - 3600000); // Default 1 hour if no iat
        const timeElapsed = now - (payload.iat ? payload.iat * 1000 : now - 3600000);
        const lifetimePercentage = timeElapsed / tokenLifetime;
        
        if (lifetimePercentage > 0.75 && timeSinceActivity < this.SESSION_TIMEOUT) {
          try {
            const { default: authService } = await import('../sanctuary/services/authService.js');
            await authService.refreshToken();
            this.updateActivity();
          } catch {
            // Silent error handling for predictive refresh
          }
        }
      }

      return true;
    } catch {
      return false;
    }
  },

  // Force logout by clearing tokens and redirecting
  async forceLogout() {
    localStorage.removeItem('sanctuary_token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('adminToken');
    // Redirect to login or reload page
    window.location.href = '/login';
  },

  // Initialize session monitoring
  startSessionMonitoring() {
    // Update activity on user interactions
    const updateActivityHandler = () => this.updateActivity();
    
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
      document.addEventListener(event, updateActivityHandler, { passive: true });
    });

    // Load last activity from storage
    const storedActivity = localStorage.getItem('sanctuary_last_activity');
    if (storedActivity) {
      this.lastActivity = parseInt(storedActivity, 10);
    }

    // Start periodic session checks
    this.sessionCheckTimer = setInterval(() => {
      this.checkSessionValidity();
    }, this.ACTIVITY_CHECK_INTERVAL);

    // Start proactive token refresh (every 50 minutes to beat 1-hour JWT expiry)
    this.tokenRefreshTimer = setInterval(async () => {
      const token = localStorage.getItem('sanctuary_token') || 
                   localStorage.getItem('authToken') || 
                   localStorage.getItem('token');
      
      if (token && (Date.now() - this.lastActivity) < this.SESSION_TIMEOUT) {
        try {
          const { default: authService } = await import('../sanctuary/services/authService.js');
          await authService.refreshToken();
          this.updateActivity();
        } catch {
          // Silent error handling for security
        }
      }
    }, this.TOKEN_REFRESH_INTERVAL);

    // Delayed initial session check to ensure everything is loaded
    setTimeout(() => {
      this.checkSessionValidity();
    }, 1000);
  },

  // Stop session monitoring
  stopSessionMonitoring() {
    if (this.sessionCheckTimer) {
      clearInterval(this.sessionCheckTimer);
      this.sessionCheckTimer = null;
    }
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
    
    // PERFORMANCE FIX: Remove activity listeners to prevent memory leaks
    const updateActivityHandler = () => this.updateActivity();
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
      document.removeEventListener(event, updateActivityHandler);
    });
  }
};

// Export default for backward compatibility
export default supabase;

// Enhanced retry logic for API calls
export const retryApiCall = async (apiFunction, maxAttempts = 3, delayMs = 1000) => {
  let lastError = null;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await apiFunction();
    } catch (error) {
      lastError = error;
      
      // Don't retry for certain errors
      if (
        error.message?.includes('invalid credentials') ||
        error.message?.includes('unauthorized') ||
        error.status === 403 ||
        attempt === maxAttempts
      ) {
        throw error;
      }
      
      // Exponential backoff
      const delay = delayMs * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};
